<template>
  <v-dialog v-model="dialogState" persistent max-width="960px">
    <v-card>
      <v-toolbar color="accent" fixed>
        <v-toolbar-title>
          <v-icon left>{{ dialogIcon }}</v-icon>
          {{ dialogTitle }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <v-form>
          <v-row align="start">
            <div class="col-xs-12 col-sm-12 col-md-6">
              <v-select
                v-model="newItem.gln_type"
                :items="glnTypes"
                item-text="text"
                item-value="value"
                :label="$vuetify.lang.t('$vuetify.gln.type') + ' *'"
                :error="showError('gln_type')"
                :error-messages="errorMessage('gln_type')"
                @change="validateForm"
              ></v-select>
            </div>

            <div class="col-xs-12 col-sm-12 col-md-6">
              <v-text-field
                v-model="newItem.name.ka"
                :label="$vuetify.lang.t('$vuetify.gln.name_ka') + ' *'"
                :error="showError('name_ka')"
                :error-messages="errorMessage('name_ka')"
                @keyup="validateForm"
              ></v-text-field>
            </div>

            <div class="col-xs-12 col-sm-12 col-md-6">
              <v-text-field
                v-model="newItem.name.en"
                :label="$vuetify.lang.t('$vuetify.gln.name_en') + ' *'"
                :error="showError('name_en')"
                :error-messages="errorMessage('name_en')"
                @keyup="validateForm"
                v-on:keypress="latinOnly($event)"
              ></v-text-field>
            </div>

            <div
              v-if="isDigitalLocation"
              class="col-xs-12 col-sm-12 col-md-6"
            >
              <v-text-field
                v-model="newItem.digital_address"
                :label="$vuetify.lang.t('$vuetify.gln.digital_address') + ' *'"
                :error="showError('digital_address')"
                :error-messages="errorMessage('digital_address')"
                @keyup="validateForm"
              ></v-text-field>
            </div>

            <div
              v-if="isFixedPhysicalLocation"
              class="col-xs-12 col-sm-12 col-md-6"
            >
              <v-radio-group
                v-model="fixedLocationMode"
                row
                :label="$vuetify.lang.t('$vuetify.gln.fixed_location_mode')"
              >
                <v-radio
                  :label="$vuetify.lang.t('$vuetify.gln.fixed_mode_address')"
                  value="address"
                ></v-radio>
                <v-radio
                  :label="$vuetify.lang.t('$vuetify.gln.fixed_mode_geo')"
                  value="geo"
                ></v-radio>
              </v-radio-group>
            </div>

            <template v-if="isFixedPhysicalLocation && fixedLocationMode === 'address'">
              <div class="col-xs-12 col-sm-12 col-md-6">
                <v-select
                  v-model="newItem.fixed_address_country_code"
                  :items="countries"
                  item-text="text"
                  item-value="code"
                  :label="$vuetify.lang.t('$vuetify.gln.country_code') + ' *'"
                  :error="showError('fixed_address_country_code')"
                  :error-messages="errorMessage('fixed_address_country_code')"
                  @change="validateForm"
                ></v-select>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-6">
                <v-text-field
                  v-model="newItem.fixed_address_locality.ka"
                  :label="$vuetify.lang.t('$vuetify.gln.locality_ka') + ' *'"
                  :error="showError('fixed_address_locality_ka')"
                  :error-messages="errorMessage('fixed_address_locality_ka')"
                  @keyup="validateForm"
                ></v-text-field>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-6">
                <v-text-field
                  v-model="newItem.fixed_address_locality.en"
                  :label="$vuetify.lang.t('$vuetify.gln.locality_en') + ' *'"
                  :error="showError('fixed_address_locality_en')"
                  :error-messages="errorMessage('fixed_address_locality_en')"
                  @keyup="validateForm"
                  v-on:keypress="latinOnly($event)"
                ></v-text-field>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-6">
                <v-text-field
                  v-model="newItem.fixed_address_postal_name.ka"
                  :label="$vuetify.lang.t('$vuetify.gln.postal_name_ka') + ' *'"
                  :error="showError('fixed_address_postal_name_ka')"
                  :error-messages="errorMessage('fixed_address_postal_name_ka')"
                  @keyup="validateForm"
                ></v-text-field>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-6">
                <v-text-field
                  v-model="newItem.fixed_address_postal_name.en"
                  :label="$vuetify.lang.t('$vuetify.gln.postal_name_en') + ' *'"
                  :error="showError('fixed_address_postal_name_en')"
                  :error-messages="errorMessage('fixed_address_postal_name_en')"
                  @keyup="validateForm"
                  v-on:keypress="latinOnly($event)"
                ></v-text-field>
              </div>
            </template>

            <template v-if="isFixedPhysicalLocation && fixedLocationMode === 'geo'">
              <div class="col-xs-12 col-sm-12 col-md-4">
                <v-text-field
                  v-model="newItem.fixed_geo_latitude"
                  :label="$vuetify.lang.t('$vuetify.gln.latitude') + ' *'"
                  :error="showError('fixed_geo_latitude')"
                  :error-messages="errorMessage('fixed_geo_latitude')"
                  @keyup="validateForm"
                ></v-text-field>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-4">
                <v-text-field
                  v-model="newItem.fixed_geo_longitude"
                  :label="$vuetify.lang.t('$vuetify.gln.longitude') + ' *'"
                  :error="showError('fixed_geo_longitude')"
                  :error-messages="errorMessage('fixed_geo_longitude')"
                  @keyup="validateForm"
                ></v-text-field>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-4">
                <v-text-field
                  v-model="newItem.fixed_geo_system"
                  :label="$vuetify.lang.t('$vuetify.gln.geo_system') + ' *'"
                  :error="showError('fixed_geo_system')"
                  :error-messages="errorMessage('fixed_geo_system')"
                  @keyup="validateForm"
                ></v-text-field>
              </div>
            </template>

            <div class="col-xs-12 col-sm-12 col-md-4">
              <v-text-field
                v-model="newItem.contact_email"
                :label="$vuetify.lang.t('$vuetify.gln.contact_email')"
                :error="showError('contact_email')"
                :error-messages="errorMessage('contact_email')"
                @keyup="validateForm"
              ></v-text-field>
            </div>

            <div class="col-xs-12 col-sm-12 col-md-4">
              <v-text-field
                v-model="newItem.contact_telephone"
                :label="$vuetify.lang.t('$vuetify.gln.contact_telephone')"
                :error="showError('contact_telephone')"
                :error-messages="errorMessage('contact_telephone')"
                @keyup="validateForm"
              ></v-text-field>
            </div>

            <div class="col-xs-12 col-sm-12 col-md-4">
              <v-text-field
                v-model="newItem.contact_website"
                :label="$vuetify.lang.t('$vuetify.gln.contact_website')"
                :error="showError('contact_website')"
                :error-messages="errorMessage('contact_website')"
                @keyup="validateForm"
              ></v-text-field>
            </div>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :disabled="isSubmitting" @click="closePopup">
          <v-icon class="mr-3">mdi-close</v-icon>
          {{ $vuetify.lang.t("$vuetify.action.cancel") }}
        </v-btn>
        <v-btn
          :loading="isSubmitting && submitMode === 'save'"
          :disabled="isSubmitting"
          @click="submitGln()"
        >
          <v-icon class="mr-3">mdi-content-save</v-icon>
          {{ submitLabel }}
        </v-btn>
        <v-btn
          v-if="canConfirmCurrentGln"
          color="success"
          :loading="isSubmitting && submitMode === 'confirm'"
          :disabled="isSubmitting"
          @click="submitGln('confirmed')"
        >
          <v-icon class="mr-3">mdi-check</v-icon>
          {{ $vuetify.lang.t("$vuetify.action.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Countries from "@/plugins/countries";
import Api from "@/services/api";

function defaultItem() {
  return {
    company_id: null,
    gln_type: null,
    name: {
      ka: "",
      en: "",
    },
    digital_address: "",
    fixed_address_country_code: "",
    fixed_address_locality: {
      ka: "",
      en: "",
    },
    fixed_address_postal_name: {
      ka: "",
      en: "",
    },
    fixed_geo_latitude: "",
    fixed_geo_longitude: "",
    fixed_geo_system: "https://epsg.io/4326",
    contact_email: "",
    contact_website: "",
    contact_telephone: "",
  };
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

export default {
  props: {
    popupAddGln: Boolean,
    orgID: [Number, String],
    glnData: {
      type: Object,
      default: null,
    },
  },
  data: function() {
    return {
      dialogState: false,
      fixedLocationMode: "address",
      newItem: defaultItem(),
      formErrors: {},
      submitAttempted: false,
      isInitializingForm: false,
      isSubmitting: false,
      submitMode: null,
      countries: Countries.forLanguage(this.$i18n.locale),
    };
  },
  computed: {
    glnTypes() {
      return this.$vuetify.lang.t("$vuetify.gln.types");
    },
    isEditMode() {
      return !!(this.glnData && this.glnData.id);
    },
    dialogTitle() {
      return this.isEditMode
        ? this.$vuetify.lang.t("$vuetify.gln.edit")
        : this.$vuetify.lang.t("$vuetify.gln.create");
    },
    dialogIcon() {
      return this.isEditMode ? "mdi-pencil" : "mdi-plus";
    },
    submitLabel() {
      return this.isEditMode
        ? this.$vuetify.lang.t("$vuetify.action.update")
        : this.$vuetify.lang.t("$vuetify.action.create");
    },
    canConfirmCurrentGln() {
      if (!this.isEditMode || !this.glnData) {
        return false;
      }

      return this.glnData.status === "review" || this.glnData.status === "unconfirmed";
    },
    isDigitalLocation() {
      return this.newItem.gln_type === "DIGITAL_LOCATION";
    },
    isFixedPhysicalLocation() {
      return this.newItem.gln_type === "FIXED_PHYSICAL_LOCATION";
    },
    hasErrors() {
      return Object.keys(this.formErrors).length > 0;
    },
  },
  watch: {
    popupAddGln: function(newVal) {
      this.dialogState = newVal;
      if (newVal) {
        this.initializeForm();
      }
    },
    glnData: {
      deep: true,
      handler(newVal) {
        if (this.dialogState && newVal && newVal.id) {
          this.populateForm(newVal);
        }
      },
    },
    "$i18n.locale": function() {
      this.countries = Countries.forLanguage(this.$i18n.locale);
    },
    newItem: {
      deep: true,
      handler() {
        if (this.isInitializingForm) {
          return;
        }

        if (this.submitAttempted) {
          this.validateForm();
        }
      },
    },
    "newItem.gln_type": function() {
      if (this.isInitializingForm) {
        return;
      }

      this.resetTypeSpecificFields();
      if (this.submitAttempted) {
        this.validateForm();
      }
    },
    fixedLocationMode: function() {
      if (this.isInitializingForm) {
        return;
      }

      this.resetFixedLocationModeFields();
      if (this.submitAttempted) {
        this.validateForm();
      }
    },
  },
  methods: {
    showError(field) {
      return this.submitAttempted && !!this.formErrors[field];
    },
    errorMessage(field) {
      if (!this.submitAttempted) {
        return null;
      }

      return this.formErrors[field] || null;
    },
    closePopup() {
      this.dialogState = false;
      this.$emit("closePopupAddGln");
      this.resetForm();
    },
    initializeForm() {
      if (this.isEditMode) {
        this.populateForm(this.glnData);
        return;
      }

      this.resetForm();
    },
    resetForm() {
      this.isInitializingForm = true;
      this.fixedLocationMode = "address";
      this.newItem = defaultItem();
      this.newItem.company_id = this.orgID || null;
      this.formErrors = {};
      this.submitAttempted = false;
      this.submitMode = null;
      this.isInitializingForm = false;
    },
    populateForm(item) {
      const nextItem = defaultItem();

      this.isInitializingForm = true;
      this.fixedLocationMode =
        hasValue(item.fixed_geo_latitude) ||
        hasValue(item.fixed_geo_longitude) ||
        hasValue(item.fixed_geo_system)
          ? "geo"
          : "address";

      nextItem.company_id =
        item.company_id || (item.company && item.company.id) || this.orgID || null;
      nextItem.gln_type = item.gln_type || null;
      nextItem.name = {
        ka: (item.name && item.name.ka) || "",
        en: (item.name && item.name.en) || "",
      };
      nextItem.digital_address = item.digital_address || "";
      nextItem.fixed_address_country_code = item.fixed_address_country_code || "";
      nextItem.fixed_address_locality = {
        ka:
          (item.fixed_address_locality && item.fixed_address_locality.ka) || "",
        en:
          (item.fixed_address_locality && item.fixed_address_locality.en) || "",
      };
      nextItem.fixed_address_postal_name = {
        ka:
          (item.fixed_address_postal_name &&
            item.fixed_address_postal_name.ka) ||
          "",
        en:
          (item.fixed_address_postal_name &&
            item.fixed_address_postal_name.en) ||
          "",
      };
      nextItem.fixed_geo_latitude = hasValue(item.fixed_geo_latitude)
        ? String(item.fixed_geo_latitude)
        : "";
      nextItem.fixed_geo_longitude = hasValue(item.fixed_geo_longitude)
        ? String(item.fixed_geo_longitude)
        : "";
      nextItem.fixed_geo_system =
        item.fixed_geo_system || nextItem.fixed_geo_system;
      nextItem.contact_email = item.contact_email || "";
      nextItem.contact_website = item.contact_website || "";
      nextItem.contact_telephone = item.contact_telephone || "";

      this.newItem = nextItem;
      this.formErrors = {};
      this.submitAttempted = false;
      this.submitMode = null;
      this.isInitializingForm = false;
    },
    resetTypeSpecificFields() {
      this.newItem.digital_address = "";
      this.newItem.fixed_address_country_code = "";
      this.newItem.fixed_address_locality = { ka: "", en: "" };
      this.newItem.fixed_address_postal_name = { ka: "", en: "" };
      this.newItem.fixed_geo_latitude = "";
      this.newItem.fixed_geo_longitude = "";
      this.newItem.fixed_geo_system = "https://epsg.io/4326";
    },
    resetFixedLocationModeFields() {
      if (this.fixedLocationMode === "address") {
        this.newItem.fixed_geo_latitude = "";
        this.newItem.fixed_geo_longitude = "";
        this.newItem.fixed_geo_system = "https://epsg.io/4326";
        return;
      }

      this.newItem.fixed_address_country_code = "";
      this.newItem.fixed_address_locality = { ka: "", en: "" };
      this.newItem.fixed_address_postal_name = { ka: "", en: "" };
    },
    validateForm() {
      const errors = {};
      const requiredMessage = this.$vuetify.lang.t("$vuetify.errors.required_field");
      const invalidEmailMessage = this.$vuetify.lang.t("$vuetify.errors.real_info");

      if (!this.newItem.gln_type) {
        errors.gln_type = requiredMessage;
      }

      if (!this.newItem.name.ka || this.newItem.name.ka.length < 2) {
        errors.name_ka = requiredMessage;
      }

      if (!this.newItem.name.en || this.newItem.name.en.length < 2) {
        errors.name_en = requiredMessage;
      }

      if (this.isDigitalLocation && !this.newItem.digital_address) {
        errors.digital_address = requiredMessage;
      }

      if (this.isFixedPhysicalLocation && this.fixedLocationMode === "address") {
        if (!this.newItem.fixed_address_country_code) {
          errors.fixed_address_country_code = requiredMessage;
        }
        if (!this.newItem.fixed_address_locality.ka) {
          errors.fixed_address_locality_ka = requiredMessage;
        }
        if (!this.newItem.fixed_address_locality.en) {
          errors.fixed_address_locality_en = requiredMessage;
        }
        if (!this.newItem.fixed_address_postal_name.ka) {
          errors.fixed_address_postal_name_ka = requiredMessage;
        }
        if (!this.newItem.fixed_address_postal_name.en) {
          errors.fixed_address_postal_name_en = requiredMessage;
        }
      }

      if (this.isFixedPhysicalLocation && this.fixedLocationMode === "geo") {
        if (this.newItem.fixed_geo_latitude === "") {
          errors.fixed_geo_latitude = requiredMessage;
        } else if (Number.isNaN(Number(this.newItem.fixed_geo_latitude))) {
          errors.fixed_geo_latitude = this.$vuetify.lang.t("$vuetify.errors.real_info");
        }

        if (this.newItem.fixed_geo_longitude === "") {
          errors.fixed_geo_longitude = requiredMessage;
        } else if (Number.isNaN(Number(this.newItem.fixed_geo_longitude))) {
          errors.fixed_geo_longitude = this.$vuetify.lang.t("$vuetify.errors.real_info");
        }

        if (!this.newItem.fixed_geo_system) {
          errors.fixed_geo_system = requiredMessage;
        }
      }

      if (
        this.newItem.contact_email &&
        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z0-9]{2,}))$/.test(
          this.newItem.contact_email
        )
      ) {
        errors.contact_email = invalidEmailMessage;
      }

      this.formErrors = errors;
    },
    buildPayload(status) {
      const payload = {
        gln_type: this.newItem.gln_type,
        name: {
          ka: this.newItem.name.ka,
          en: this.newItem.name.en,
        },
      };

      if (this.isEditMode) {
        payload.id = this.glnData.id;
      } else {
        payload.company_id = this.newItem.company_id || this.orgID;
      }

      if (this.newItem.contact_email) {
        payload.contact_email = this.newItem.contact_email;
      }
      if (this.newItem.contact_website) {
        payload.contact_website = this.newItem.contact_website;
      }
      if (this.newItem.contact_telephone) {
        payload.contact_telephone = this.newItem.contact_telephone;
      }

      if (this.isDigitalLocation) {
        payload.digital_address = this.newItem.digital_address;
      }

      if (this.isFixedPhysicalLocation && this.fixedLocationMode === "address") {
        payload.fixed_address_country_code = this.newItem.fixed_address_country_code;
        payload.fixed_address_locality = {
          ka: this.newItem.fixed_address_locality.ka,
          en: this.newItem.fixed_address_locality.en,
        };
        payload.fixed_address_postal_name = {
          ka: this.newItem.fixed_address_postal_name.ka,
          en: this.newItem.fixed_address_postal_name.en,
        };
      }

      if (this.isFixedPhysicalLocation && this.fixedLocationMode === "geo") {
        payload.fixed_geo_latitude = Number(this.newItem.fixed_geo_latitude);
        payload.fixed_geo_longitude = Number(this.newItem.fixed_geo_longitude);
        payload.fixed_geo_system = this.newItem.fixed_geo_system;
      }

      if (status) {
        payload.status = status;
      }

      return payload;
    },
    submitGln(status) {
      this.submitAttempted = true;
      this.validateForm();
      if (this.hasErrors) {
        return;
      }

      const action = this.isEditMode ? "update" : "create";
      const eventName = this.isEditMode ? "updated" : "created";
      const toastKey = this.isEditMode
        ? "$vuetify.action.update_success"
        : "$vuetify.action.create_success";

      this.isSubmitting = true;
      this.submitMode = status ? "confirm" : "save";
      Api.gln(action, this.buildPayload(status))
        .then(({ data }) => {
          this.$emit(eventName, data);
          this.$toast.ok(this.$vuetify.lang.t(toastKey));
          this.closePopup();
        })
        .finally(() => {
          this.isSubmitting = false;
          this.submitMode = null;
        });
    },
  },
};
</script>
